import { useI18n } from "@lookiero/i18n-react";
import { Country, CURRENCY } from "@lookiero/sty-psp-locale";
import {
  AnswersProjection,
  questionById,
  QuestionProjection,
  QuestionType,
} from "@lookiero/sty-psp-quiz-style-profile-common-ui";
import { splitDateString } from "@lookiero/sty-psp-quiz-style-profile-common-ui/dist/src/infrastructure/ui/components/organisms/question/behaviours/dateUtils";
import {
  splitImperialHeightString,
  splitImperialWeightString,
} from "@lookiero/sty-psp-quiz-style-profile-common-ui/dist/src/infrastructure/ui/components/organisms/question/behaviours/mapAnswersToImperialSystem";
import { Segment } from "@lookiero/sty-psp-segment";
import { isUuid } from "@lookiero/sty-psp-uuid";
import { DOMAIN } from "../../../i18n/i18n";
import { sortQuestionAnswersByDefinition } from "./sortQuestionAnswersByDefinition";

const EMPTY_ANSWER = "-";

interface UseQuestionAnswersFunctionArgs {
  readonly question: QuestionProjection;
  readonly answers: AnswersProjection;
  readonly country: Country;
  readonly segment: Segment;
}

interface UseQuestionAnswersFunction {
  (args: UseQuestionAnswersFunctionArgs): string;
}

const useQuestionAnswers: UseQuestionAnswersFunction = ({ question, answers, segment, country }) => {
  const { formatMessage, formatNumber } = useI18n({ domain: DOMAIN });

  const sortedAnswers: AnswersProjection = sortQuestionAnswersByDefinition({ question, answers });

  const allQuestionIds = Object.keys(sortedAnswers);

  if (
    allQuestionIds[0] &&
    questionById({ questions: [question], questionId: allQuestionIds[0] })?.type === QuestionType.HOST_IMAGE_UPLOAD
  ) {
    return String(allQuestionIds.length);
  }

  const allAnswers = Object.entries(sortedAnswers)
    .reduce((acc, [questionId, questionAnswers]) => {
      const questionType = questionById({ questions: [question], questionId })?.type;

      if (!questionType) {
        return [...acc, EMPTY_ANSWER];
      }

      switch (questionType) {
        case QuestionType.HOST_DATE:
          const { year, month, day } = splitDateString({ date: questionAnswers[0] });

          return [...acc, `${day}/${month}/${year}`];
        case QuestionType.HOST_HEIGHT_IMPERIAL:
          const { feet, inches } = splitImperialHeightString({ height: questionAnswers[0] });

          return [...acc, `${feet} ft ${inches} in`];
        case QuestionType.HOST_HEIGHT_METRIC:
          return [...acc, `${questionAnswers[0]} cm`];
        case QuestionType.HOST_WEIGHT_IMPERIAL:
          const { pounds, stones } = splitImperialWeightString({ weight: questionAnswers[0] });

          return [...acc, `${stones} st ${pounds} lb`];
        case QuestionType.HOST_WEIGHT_METRIC:
          return [...acc, `${questionAnswers[0]} kg`];
        default:
          return [
            ...acc,
            ...questionAnswers.reduce((answersAcc, answer) => {
              if (!isUuid(answer)) {
                return [...answersAcc, answer];
              }

              const answerQuestion = questionById({ questions: [question], questionId: answer });

              return answerQuestion?.translationKey
                ? answerQuestion.type === QuestionType.NUMBER_WITH_CURRENCY
                  ? [
                      ...answersAcc,
                      formatNumber(Number(answerQuestion.translationKey), {
                        style: "currency",
                        currency: CURRENCY[country],
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }),
                    ]
                  : [
                      ...answersAcc,
                      formatMessage({ id: answerQuestion.translationKey }, { segment: segment.toLowerCase() }),
                    ]
                : answersAcc;
            }, [] as string[]),
          ];
      }
    }, [] as string[])
    .join(", ");

  return allAnswers.length > 0 ? allAnswers : EMPTY_ANSWER;
};

export { useQuestionAnswers, EMPTY_ANSWER };
